/**
 * "1 input changed": this experiment scored with the evaluator but read some
 * variables from other paths than that version's default. Clicking the badge
 * says which, in plain words: "This evaluator read {{words}} from output.reason
 * instead of its default."
 */

import { Popover } from "./popover";

export function RemappedBadge({
  version,
  remapped,
}: {
  version?: number | null | undefined;
  remapped?: Record<string, string> | undefined;
}) {
  const entries = Object.entries(remapped ?? {});
  const n = entries.length;
  if (n === 0) return null;
  const text = `${n} ${n === 1 ? "input" : "inputs"} changed`;
  return (
    <Popover
      trigger={text}
      triggerLabel={`${text}: show which`}
      triggerClassName="pill pill-warn cursor-pointer hover:underline"
      label="Changed inputs"
      className="w-[300px] px-3.5 py-3 text-xs leading-normal font-normal whitespace-normal text-body"
    >
      {n === 1 ? (
        <p>
          This evaluator read <Var name={entries[0]![0]} /> from <Path path={entries[0]![1]} /> instead of its
          default.
        </p>
      ) : (
        <>
          <p>This evaluator read these variables from other places than its default:</p>
          <ul className="mt-2 flex flex-col gap-1">
            {entries.map(([v, p]) => (
              <li key={v} className="flex flex-wrap items-center gap-1">
                <Var name={v} /> <span className="text-muted">from</span> <Path path={p} />
              </li>
            ))}
          </ul>
        </>
      )}
      {version ? <p className="mt-2 text-[11px] text-muted">Scored with v{version}.</p> : null}
    </Popover>
  );
}

const Var = ({ name }: { name: string }) => <code className="font-mono text-ink">{`{{${name}}}`}</code>;
const Path = ({ path }: { path: string }) => (
  <code className="rounded bg-chip px-1 py-px font-mono text-chip-ink">{path}</code>
);
